import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Wedding } from '../../types';
import { WeddingRowCard } from './WeddingRowCard';
import { GroupedWeddings } from '../../lib/weddings/categorize';
import { getAccessLevel } from '../../lib/weddings/labels';

export interface WeddingsSectionsProps {
  grouped: GroupedWeddings;
  userId: string;
  memberRoles: Record<string, string>; // wedding_id -> role
  isSuperAdmin?: boolean; // SUPER_ADMIN has full access to all weddings
  onWeddingClick: (weddingId: string) => void;
  onWeddingEdit?: (wedding: Wedding) => void;
  onWeddingDelete?: (weddingId: string) => void;
}

interface SectionsState {
  activeOpen: boolean;
  draftsOpen: boolean;
  pastOpen: boolean;
}

const STORAGE_KEY = 'weddings.sectionsState';

const DEFAULT_STATE: SectionsState = {
  activeOpen: true,
  draftsOpen: false,
  pastOpen: false,
};

function loadSectionsState(): SectionsState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_STATE, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn('Failed to load sections state from localStorage', e);
  }
  return DEFAULT_STATE;
}

function saveSectionsState(state: SectionsState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('Failed to save sections state to localStorage', e);
  }
}

interface SectionProps {
  title: string;
  count: number;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, count, isOpen, onToggle, children }) => {
  return (
    <div className="mb-6">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 py-2 text-left transition-colors"
      >
        {isOpen ? (
          <ChevronUp size={14} className="text-slate-300" />
        ) : (
          <ChevronDown size={14} className="text-slate-300" />
        )}
        <h2 className="text-ds-meta text-slate-500 dark:text-slate-400">
          {title}
        </h2>
        <span className="text-ds-meta text-slate-300 dark:text-slate-600">
          {count}
        </span>
      </button>
      
      {isOpen && (
        <div className="mt-3 space-y-2">
          {count === 0 ? (
            <div className="text-center py-8 text-slate-300 dark:text-slate-600">
              <p className="text-ds-body">Žádné svatby v této kategorii</p>
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
};

export const WeddingsSections: React.FC<WeddingsSectionsProps> = ({
  grouped,
  userId,
  memberRoles,
  isSuperAdmin = false,
  onWeddingClick,
  onWeddingEdit,
  onWeddingDelete,
}) => {
  const [state, setState] = useState<SectionsState>(loadSectionsState);

  useEffect(() => {
    saveSectionsState(state);
  }, [state]);

  const toggleSection = (section: keyof SectionsState) => {
    setState((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <div>
      <Section
        title="Aktivní"
        count={grouped.active.length}
        isOpen={state.activeOpen}
        onToggle={() => toggleSection('activeOpen')}
      >
        {grouped.active.map((wedding) => {
          const role = isSuperAdmin ? 'admin' : memberRoles[wedding.id];
          const access = isSuperAdmin ? 'admin' : getAccessLevel(wedding, userId, role);
          const isOwner = wedding.owner_user_id && wedding.owner_user_id === userId;
          const canEdit = isSuperAdmin || access === 'owner' || access === 'admin' || isOwner;
          const canDelete = canEdit;
          
          return (
            <WeddingRowCard
              key={wedding.id}
              wedding={wedding}
              myRole={role}
              myAccess={access}
              onOpen={() => onWeddingClick(wedding.id)}
              onEdit={canEdit && onWeddingEdit ? () => onWeddingEdit(wedding) : undefined}
              onDelete={canDelete && onWeddingDelete ? () => onWeddingDelete(wedding.id) : undefined}
            />
          );
        })}
      </Section>

      <Section
        title="Neaktivní / koncepty"
        count={grouped.draft.length}
        isOpen={state.draftsOpen}
        onToggle={() => toggleSection('draftsOpen')}
      >
        {grouped.draft.map((wedding) => {
          const role = isSuperAdmin ? 'admin' : memberRoles[wedding.id];
          const access = isSuperAdmin ? 'admin' : getAccessLevel(wedding, userId, role);
          const isOwner = wedding.owner_user_id && wedding.owner_user_id === userId;
          const canEdit = isSuperAdmin || access === 'owner' || access === 'admin' || isOwner;
          const canDelete = canEdit;
          
          return (
            <WeddingRowCard
              key={wedding.id}
              wedding={wedding}
              myRole={role}
              myAccess={access}
              onOpen={() => onWeddingClick(wedding.id)}
              onEdit={canEdit && onWeddingEdit ? () => onWeddingEdit(wedding) : undefined}
              onDelete={canDelete && onWeddingDelete ? () => onWeddingDelete(wedding.id) : undefined}
            />
          );
        })}
      </Section>

      <Section
        title="Uplýnulé"
        count={grouped.past.length}
        isOpen={state.pastOpen}
        onToggle={() => toggleSection('pastOpen')}
      >
        {grouped.past.map((wedding) => {
          const role = isSuperAdmin ? 'admin' : memberRoles[wedding.id];
          const access = isSuperAdmin ? 'admin' : getAccessLevel(wedding, userId, role);
          const isOwner = wedding.owner_user_id && wedding.owner_user_id === userId;
          const canEdit = isSuperAdmin || access === 'owner' || access === 'admin' || isOwner;
          const canDelete = canEdit;
          
          return (
            <WeddingRowCard
              key={wedding.id}
              wedding={wedding}
              myRole={role}
              myAccess={access}
              onOpen={() => onWeddingClick(wedding.id)}
              onEdit={canEdit && onWeddingEdit ? () => onWeddingEdit(wedding) : undefined}
              onDelete={canDelete && onWeddingDelete ? () => onWeddingDelete(wedding.id) : undefined}
            />
          );
        })}
      </Section>
    </div>
  );
};
